import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Plus, 
  Search, 
  FileText,
  Mail,
  Phone,
  MapPin,
  MoreVertical,
  Trash2,
  Edit2,
  Tag,
  X
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const ClientsPage = () => {
  const { api } = useContext(AuthContext);
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    tax_year: new Date().getFullYear() - 1,
    tags: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [tagFilter, setTagFilter] = useState("all");
  const [allTags, setAllTags] = useState([]);
  const [newTag, setNewTag] = useState("");

  useEffect(() => {
    fetchClients();
    fetchTags();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get("/tags");
      setAllTags(response.data);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !newClient.tags.includes(newTag.trim())) {
      setNewClient({ ...newClient, tags: [...newClient.tags, newTag.trim()] });
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setNewClient({ ...newClient, tags: newClient.tags.filter(t => t !== tagToRemove) });
  };

  const handleCreateClient = async () => {
    if (!newClient.name.trim()) {
      toast.error("Client name is required");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post("/clients", newClient);
      setClients([response.data, ...clients]);
      setIsCreateDialogOpen(false);
      setNewClient({
        name: "",
        email: "",
        phone: "",
        address: "",
        tax_year: new Date().getFullYear() - 1,
        tags: [],
      });
      toast.success("Client created successfully");
      navigate(`/clients/${response.data.client_id}`);
      // Refresh tags
      fetchTags();
    } catch (error) {
      console.error("Error creating client:", error);
      toast.error("Failed to create client");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClient = async (clientId, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this client? This will also delete all related documents and analyses.")) {
      return;
    }

    try {
      await api.delete(`/clients/${clientId}`);
      setClients(clients.filter(c => c.client_id !== clientId));
      toast.success("Client deleted successfully");
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error("Failed to delete client");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="status-pending text-xs">Pending</Badge>;
      case "in_progress":
        return <Badge className="status-in-progress text-xs">In Progress</Badge>;
      case "completed":
        return <Badge className="status-completed text-xs">Completed</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{status}</Badge>;
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          client.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || client.status === statusFilter;
    const matchesTag = tagFilter === "all" || (client.tags && client.tags.includes(tagFilter));
    return matchesSearch && matchesStatus && matchesTag;
  });

  const currentYear = new Date().getFullYear();
  const taxYears = Array.from({ length: 5 }, (_, i) => currentYear - 1 - i);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="clients-title">
              Clients
            </h1>
            <p className="text-slate-500 font-body mt-1">
              Manage your client portfolio and tax returns
            </p>
          </div>
          <Button 
            onClick={() => setIsCreateDialogOpen(true)}
            data-testid="create-client-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md shadow-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 font-body rounded-md border-slate-200"
              data-testid="search-clients-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px] font-body rounded-md border-slate-200" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          {allTags.length > 0 && (
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="w-full sm:w-[180px] font-body rounded-md border-slate-200" data-testid="tag-filter">
                <Tag className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Filter by tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                {allTags.map(tag => (
                  <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Clients Table */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-sm" data-testid="clients-table-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : filteredClients.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-body text-slate-500 uppercase tracking-wider text-xs">Client</TableHead>
                    <TableHead className="font-body text-slate-500 uppercase tracking-wider text-xs">Contact</TableHead>
                    <TableHead className="font-body text-slate-500 uppercase tracking-wider text-xs">Tax Year</TableHead>
                    <TableHead className="font-body text-slate-500 uppercase tracking-wider text-xs">Tags</TableHead>
                    <TableHead className="font-body text-slate-500 uppercase tracking-wider text-xs">Status</TableHead>
                    <TableHead className="font-body text-slate-500 uppercase tracking-wider text-xs w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow 
                      key={client.client_id}
                      className="cursor-pointer hover:bg-slate-50/50 transition-colors duration-200"
                      onClick={() => navigate(`/clients/${client.client_id}`)}
                      data-testid={`client-row-${client.client_id}`}
                    >
                      <TableCell className="font-body">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-heading font-semibold text-slate-600">
                              {client.name?.charAt(0)?.toUpperCase() || "C"}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{client.name}</p>
                            {client.address && (
                              <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                <MapPin className="w-3 h-3" />
                                {client.address}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-body text-sm">
                        <div className="space-y-1">
                          {client.email && (
                            <p className="text-slate-600 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {client.email}
                            </p>
                          )}
                          {client.phone && (
                            <p className="text-slate-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {client.phone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-slate-600">
                        {client.tax_year}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {client.tags?.map(tag => (
                            <Badge key={tag} variant="outline" className="text-xs bg-slate-50">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(client.status)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`client-menu-${client.client_id}`}>
                              <MoreVertical className="w-4 h-4 text-slate-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/clients/${client.client_id}`);
                              }}
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => handleDeleteClient(client.client_id, e)}
                              className="text-rose-600 focus:text-rose-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 p-8">
                <FileText className="w-16 h-16 text-slate-200 mb-4" />
                <p className="text-slate-500 font-body text-center">
                  {searchQuery || statusFilter !== "all" 
                    ? "No clients match your filters" 
                    : "No clients yet. Add your first client to get started."}
                </p>
                {!searchQuery && statusFilter === "all" && (
                  <Button 
                    onClick={() => setIsCreateDialogOpen(true)}
                    className="mt-4 bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Client
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Client Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent 
            className="sm:max-w-lg" 
            data-testid="create-client-dialog"
            onPointerDownOutside={(e) => selectOpen && e.preventDefault()}
            onEscapeKeyDown={(e) => selectOpen && e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Add New Client</DialogTitle>
              <DialogDescription className="font-body text-slate-500">
                Enter the client's information to create a new profile.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-body text-sm font-medium">
                  Client Name <span className="text-rose-500">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="John & Jane Smith"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  className="font-body rounded-md"
                  data-testid="new-client-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="font-body text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="client@email.com"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  className="font-body rounded-md"
                  data-testid="new-client-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="font-body text-sm font-medium">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="(555) 123-4567"
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    className="font-body rounded-md"
                    data-testid="new-client-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tax_year" className="font-body text-sm font-medium">Tax Year</Label>
                  <Select 
                    open={selectOpen}
                    onOpenChange={setSelectOpen}
                    value={newClient.tax_year.toString()} 
                    onValueChange={(value) => setNewClient({ ...newClient, tax_year: parseInt(value) })}
                  >
                    <SelectTrigger className="font-body rounded-md" data-testid="new-client-tax-year">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {taxYears.map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address" className="font-body text-sm font-medium">Address</Label>
                <Input
                  id="address"
                  placeholder="123 Main St, City, State 12345"
                  value={newClient.address}
                  onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                  className="font-body rounded-md"
                  data-testid="new-client-address"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm font-medium">Tags (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a tag (e.g., Business, Priority)"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
                    className="font-body rounded-md"
                    data-testid="new-client-tag-input"
                  />
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={handleAddTag}
                    className="shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {newClient.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newClient.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <X 
                          className="w-3 h-3 cursor-pointer hover:text-rose-500" 
                          onClick={() => handleRemoveTag(tag)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsCreateDialogOpen(false)}
                className="font-body rounded-md"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateClient}
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800 text-white font-body rounded-md"
                data-testid="submit-new-client"
              >
                {submitting ? "Creating..." : "Create Client"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ClientsPage;
